import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import Popover from "@material-ui/core/Popover";
import Fade from "@material-ui/core/Fade";
import Tooltip from "@material-ui/core/Tooltip";
import DeleteOutline from "@material-ui/icons/DeleteOutline";
import AttachFile from "@material-ui/icons/AttachFile";
import Close from "@material-ui/icons/Close";
import Edit from "@material-ui/icons/Edit";
import Check from "@material-ui/icons/Check";
import ArrowDropDown from "@material-ui/icons/ArrowDropDown";
import { ThreeDots } from "react-loader-spinner";
import { EditorState, ContentState, convertToRaw } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import fileDownload from "js-file-download";
import { useTranslation } from "react-i18next";
import useThemeColors from "../../hooks/useThemeColors";
import { useFeedback } from "./FeedbackContext";
import { feedbackApi } from "./feedbackApi";
import {
  STATUS_OPTIONS,
  CATEGORY_OPTIONS,
  getDisplayStatus,
} from "./feedbackConstants";
import {
  topicDetailStyles,
  STATUS_COLORS,
  editorToolbarConfig,
} from "./feedbackStyles";
import FeedbackCommentForm from "./FeedbackCommentForm";

const useStyles = makeStyles(topicDetailStyles);

function formatDateTime(iso) {
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, "0");
  const mon = String(d.getMonth() + 1).padStart(2, "0");
  const hr = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${day}/${mon} ${hr}:${min}`;
}

function getInitials(name) {
  if (!name || !name.trim()) return "?";
  return name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const AVATAR_COLORS = [
  "#5c6bc0",
  "#26a69a",
  "#ef5350",
  "#ffa726",
  "#7e57c2",
  "#66bb6a",
];
function avatarColor(userId) {
  const s = String(userId);
  let hash = 0;
  for (let i = 0; i < s.length; i++)
    hash = s.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function htmlToEditorState(html) {
  if (!html) return EditorState.createEmpty();
  const blocksFromHtml = htmlToDraft(html);
  const { contentBlocks, entityMap } = blocksFromHtml;
  const contentState = ContentState.createFromBlockArray(
    contentBlocks,
    entityMap,
  );
  return EditorState.createWithContent(contentState);
}

export default function FeedbackTopicDetail({ clientColor }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const { clientTextColor } = useThemeColors();
  const {
    selectedTopic: topic,
    updateTopic,
    deleteTopic,
    updateComment,
    deleteComment,
    currentUser,
    saving,
    saved,
    pendingAiTitleIds,
  } = useFeedback();

  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmDeleteTopic, setConfirmDeleteTopic] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editEditorState, setEditEditorState] = useState(
    EditorState.createEmpty(),
  );

  useEffect(() => {
    if (topic) {
      setEditingTitle(false);
      setConfirmDelete(null);
      setConfirmDeleteTopic(false);
      setEditingCommentId(null);
    }
  }, [topic?.id]);

  if (!topic) return null;

  const displayStatus = getDisplayStatus(topic);
  const statusColor = STATUS_COLORS[displayStatus] || STATUS_COLORS.open;
  const isArchived = displayStatus === "archived";
  const comments = topic.comments || [];
  const isPendingTitle = pendingAiTitleIds.has(topic.id);

  const handleTitleBlur = () => {
    setEditingTitle(false);
    const trimmed = titleDraft.trim();
    if (trimmed && trimmed !== topic.title && !saving) {
      updateTopic(topic.id, { title: trimmed });
    }
  };

  const handleStatusChange = (newStatus) => {
    if (saving) return;
    updateTopic(topic.id, { status: newStatus });
  };

  const handleCategoryChange = (e) => {
    if (saving) return;
    updateTopic(topic.id, { category: e.target.value });
  };

  const handleDeleteTopic = () => {
    if (saving) return;
    if (!confirmDeleteTopic) {
      setConfirmDeleteTopic(true);
      return;
    }
    deleteTopic(topic.id);
  };

  const handleDeleteComment = (commentId) => {
    if (saving) return;
    if (confirmDelete !== commentId) {
      setConfirmDelete(commentId);
      return;
    }
    deleteComment(topic.id, commentId);
    setConfirmDelete(null);
  };

  const startEditComment = useCallback((comment) => {
    setEditingCommentId(comment.id);
    setEditEditorState(htmlToEditorState(comment.content_html));
  }, []);

  const cancelEditComment = useCallback(() => {
    setEditingCommentId(null);
  }, []);

  const saveEditComment = useCallback(() => {
    if (!editingCommentId || saving) return;
    const html = draftToHtml(convertToRaw(editEditorState.getCurrentContent()));
    const text = editEditorState.getCurrentContent().getPlainText().trim();
    if (!text) return;
    updateComment(topic.id, editingCommentId, html);
    setEditingCommentId(null);
  }, [editingCommentId, editEditorState, topic?.id, updateComment, saving]);

  const [downloadingAttId, setDownloadingAttId] = useState(null);

  const handleDownloadAttachment = useCallback((attachmentId, fileName) => {
    setDownloadingAttId(attachmentId);
    feedbackApi
      .downloadAttachment(attachmentId)
      .then((res) => fileDownload(res.data, fileName))
      .catch((err) => console.error("[Feedback] Download failed", err))
      .finally(() => setDownloadingAttId(null));
  }, []);

  const [statusAnchor, setStatusAnchor] = useState(null);

  const reversedComments = [...comments].reverse();

  return (
    <div className={classes.detailContainer}>
      {/* Header */}
      <div className={classes.detailHeader}>
        <div className={classes.titleRow}>
          {isPendingTitle ? (
            <span
              className={classes.titleDisplay}
              style={{ fontStyle: "italic", color: "#999" }}
            >
              {t("feedback.defining_topic_name")}
            </span>
          ) : editingTitle ? (
            <input
              className={classes.titleInput}
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={handleTitleBlur}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleTitleBlur();
                if (e.key === "Escape") setEditingTitle(false);
              }}
              autoFocus
            />
          ) : (
            <span
              className={classes.titleDisplay}
              onClick={() => {
                setTitleDraft(topic.title);
                setEditingTitle(true);
              }}
            >
              {topic.title}
            </span>
          )}

          <span
            className={classes.deleteButton}
            onClick={handleDeleteTopic}
            style={saving ? { opacity: 0.4, pointerEvents: "none" } : undefined}
          >
            {confirmDeleteTopic ? (
              <>
                <span>{t("feedback.confirm_delete")}</span>
                <DeleteOutline style={{ fontSize: "15px" }} />
              </>
            ) : (
              <DeleteOutline style={{ fontSize: "17px" }} />
            )}
          </span>
        </div>

        <div className={classes.headerMeta}>
          {isArchived ? (
            <span
              className={classes.statusTrigger}
              style={{
                backgroundColor: statusColor.text,
                cursor: "default",
              }}
            >
              {t("feedback.status_archived")}
            </span>
          ) : (
            <>
              <button
                className={classes.statusTrigger}
                style={{ backgroundColor: statusColor.text }}
                onClick={(e) => !saving && setStatusAnchor(e.currentTarget)}
                disabled={saving}
              >
                {t(`feedback.status_${topic.status}`)}
                <ArrowDropDown
                  style={{ fontSize: "14px", margin: "-2px -4px -2px 0" }}
                />
              </button>
              <Popover
                open={Boolean(statusAnchor)}
                anchorEl={statusAnchor}
                onClose={() => setStatusAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{ style: { marginTop: 2 } }}
              >
                <div className={classes.statusPopoverContent}>
                  {STATUS_OPTIONS.map((s) => {
                    const sc = STATUS_COLORS[s.value] || STATUS_COLORS.open;
                    const selected = topic.status === s.value;
                    return (
                      <div
                        key={s.value}
                        className={classes.statusPopoverItem}
                        style={selected ? { fontWeight: 500 } : undefined}
                        onClick={() => {
                          handleStatusChange(s.value);
                          setStatusAnchor(null);
                        }}
                      >
                        <span
                          className={classes.statusPopoverDot}
                          style={{ backgroundColor: sc.text }}
                        />
                        {t(s.labelKey)}
                        {selected && (
                          <Check className={classes.statusPopoverCheck} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </Popover>
            </>
          )}

          {topic.status === "pending_validation" && (
            <>
              <Tooltip
                title={t("feedback.validate_tooltip")}
                placement="bottom"
              >
                <button
                  className={classes.validateBtn}
                  onClick={() => handleStatusChange("completed")}
                  disabled={saving}
                >
                  {t("feedback.validate")}
                </button>
              </Tooltip>
              <Tooltip
                title={t("feedback.invalidate_tooltip")}
                placement="bottom"
              >
                <button
                  className={classes.invalidateBtn}
                  onClick={() => handleStatusChange("open")}
                  disabled={saving}
                >
                  {t("feedback.invalidate")}
                </button>
              </Tooltip>
            </>
          )}

          <select
            className={classes.categorySelect}
            value={topic.category || ""}
            onChange={handleCategoryChange}
            disabled={saving}
          >
            <option value="">{t("feedback.no_category")}</option>
            {CATEGORY_OPTIONS.map((c) => (
              <option key={c.value} value={c.value}>
                {t(c.labelKey)}
              </option>
            ))}
          </select>

          <span className={classes.detailInfo}>
            {t("feedback.created_by_on", {
              name: topic.created_by_name,
              date: formatDateTime(topic.created_at),
            })}
          </span>

          <Fade in={saving || saved} timeout={{ enter: 200, exit: 400 }}>
            <span className={classes.saveIndicator}>
              {saving ? (
                <ThreeDots
                  height={12}
                  width={24}
                  color="#999"
                  ariaLabel="saving"
                />
              ) : (
                <>
                  <Check style={{ fontSize: "14px", color: "#4caf50" }} />
                  <span style={{ color: "#4caf50" }}>
                    {t("feedback.saved")}
                  </span>
                </>
              )}
            </span>
          </Fade>
        </div>
      </div>

      {/* Comments - latest first */}
      <div className={classes.commentsSection}>
        {comments.length === 0 && (
          <div
            style={{
              textAlign: "center",
              padding: "30px 0",
              color: "#999",
              fontSize: "13px",
            }}
          >
            {t("feedback.no_comments_yet")}
          </div>
        )}

        {reversedComments.map((comment) => {
          const isOwner = comment.author_id === currentUser.id;
          const isEditing = editingCommentId === comment.id;
          const isConfirming = confirmDelete === comment.id;
          const attachments = comment.attachments || [];

          return (
            <div key={comment.id} className={classes.commentCard}>
              <div className={classes.commentHeader}>
                <div className={classes.commentAuthorRow}>
                  <div
                    className={classes.avatar}
                    style={{
                      backgroundColor: avatarColor(comment.author_id),
                    }}
                  >
                    {getInitials(comment.author_name)}
                  </div>
                  <span className={classes.commentAuthor}>
                    {comment.author_name}
                  </span>
                  <span className={classes.commentDate}>
                    {formatDateTime(comment.created_at)}
                  </span>
                </div>

                {isOwner && !isEditing && (
                  <div
                    className={classes.commentActions}
                    style={
                      saving
                        ? { opacity: 0.4, pointerEvents: "none" }
                        : undefined
                    }
                  >
                    <Edit
                      className={classes.actionIcon}
                      onClick={() => startEditComment(comment)}
                    />
                    <span
                      onClick={() => handleDeleteComment(comment.id)}
                      style={{ display: "flex" }}
                    >
                      {isConfirming ? (
                        <span className={classes.confirmDeleteOverlay}>
                          {t("feedback.confirm_delete")}
                          <Close className={classes.deleteIcon} />
                        </span>
                      ) : (
                        <Close className={classes.deleteIcon} />
                      )}
                    </span>
                  </div>
                )}
              </div>

              {isEditing ? (
                <>
                  <div className={classes.commentEditWrapper}>
                    <Editor
                      editorState={editEditorState}
                      onEditorStateChange={setEditEditorState}
                      toolbar={editorToolbarConfig}
                    />
                  </div>
                  <div className={classes.commentEditActions}>
                    <button
                      className={classes.editActionBtn}
                      style={{ background: "#e4e4e4", color: "#555" }}
                      onClick={cancelEditComment}
                    >
                      {t("cancel")}
                    </button>
                    <button
                      className={classes.editActionBtn}
                      style={{
                        background: clientColor || "#555",
                        color: clientTextColor,
                        ...(saving
                          ? { opacity: 0.5, pointerEvents: "none" }
                          : {}),
                      }}
                      onClick={saveEditComment}
                      disabled={saving}
                    >
                      {t("feedback.save")}
                    </button>
                  </div>
                </>
              ) : (
                <div
                  className={classes.commentBody}
                  dangerouslySetInnerHTML={{ __html: comment.content_html }}
                />
              )}

              {attachments.length > 0 && (
                <div className={classes.attachmentList}>
                  {attachments.map((att) => {
                    const isDownloading = downloadingAttId === att.id;
                    return (
                      <span
                        key={att.id}
                        className={classes.attachmentChip}
                        style={
                          isDownloading
                            ? { opacity: 0.6, pointerEvents: "none" }
                            : undefined
                        }
                        onClick={() =>
                          handleDownloadAttachment(att.id, att.file_name)
                        }
                      >
                        {isDownloading ? (
                          <ThreeDots
                            height={8}
                            width={14}
                            color="#999"
                            ariaLabel="downloading"
                          />
                        ) : (
                          <AttachFile style={{ fontSize: "11px" }} />
                        )}
                        {att.file_name} ({formatSize(att.file_size)})
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* New comment form */}
      <FeedbackCommentForm topicId={topic.id} clientColor={clientColor} />
    </div>
  );
}

FeedbackTopicDetail.propTypes = {
  clientColor: PropTypes.string,
};
